import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../Common/Configurations/AuthContext";
import apiService from "../../Common/Configurations/APIService";
import config from "../../Common/Configurations/APIConfig";
import Modal from "react-bootstrap/Modal";
import Button from "react-bootstrap/Button";
import { toast } from "react-toastify";
import MarketingTabs from "../AllTabs/MarketingTabs";
import Header from "../../Common/Layout/Header";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import CustomerShareModal from "./CustomerShareModal";

const initialPromoValues = {
  promotionId: "00000000-0000-0000-0000-000000000000",
  title: "",
  content: "",
  imageFile: null,
};

const Promotions = () => {
  const { user, refreshUserProfile } = useAuth();
  const [promotions, setPromotions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [promoToEdit, setPromoToEdit] = useState(initialPromoValues);
  const [previewImage, setPreviewImage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // --- STEP 1: Add state for errors ---
  const [errors, setErrors] = useState({});

  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedPromo, setSelectedPromo] = useState(null);

  const [showViewModal, setShowViewModal] = useState(false);
  const [promoToView, setPromoToView] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [promoToDelete, setPromoToDelete] = useState(null);

  const handleWhatsappShareClick = async (promo) => {
    const updatedProfile = await refreshUserProfile();
    if (updatedProfile && updatedProfile.connects > 0) {
      handleSendModalOpen(promo);
    } else {
      toast.error(
        "You have no WhatsApp connects left. Please recharge to continue."
      );
    }
  };

  const getPromotions = useCallback(async () => {
    if (!user?.id) return;
    setIsLoading(true);
    try {
      const data = await apiService.getById(config.GETPROMOTIONBYUSER, user.id);
      setPromotions(data);
    } catch (error) {
      console.error("Failed to fetch promotions", error);
      toast.error("Could not load promotions.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    getPromotions();
  }, [getPromotions]);

  const handleAddModalOpen = () => {
    setIsEditMode(false);
    setPromoToEdit(initialPromoValues);
    setPreviewImage(null);
    setErrors({}); // Clear previous errors
    setShowAddEditModal(true);
  };

  const handleEditModalOpen = (promo) => {
    setIsEditMode(true);
    setPromoToEdit({
      promotionId: promo.promotionId,
      title: promo.title,
      content: promo.content,
      imageFile: null,
    });
    setPreviewImage(promo.imageSrc);
    setErrors({}); // Clear previous errors
    setShowAddEditModal(true);
  };

  const handleAddEditModalClose = () => {
    setShowAddEditModal(false);
    setErrors({}); // Clear errors on close
  };

  const handlePromoInputChange = (e) => {
    const { name, value } = e.target;
    setPromoToEdit({ ...promoToEdit, [name]: value });
  };

  const handleImageChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setPromoToEdit({ ...promoToEdit, imageFile: file });
      setPreviewImage(URL.createObjectURL(file));
      // Clear the image error when a file is selected
      if (errors.imageFile === false) {
        setErrors((prev) => ({ ...prev, imageFile: true }));
      }
    }
  };

  // --- STEP 2: Create validate function and applyErrorClass helper ---
  const validate = () => {
    let tempErrors = {};
    // Image is only required when creating a new promotion
    tempErrors.imageFile = isEditMode || promoToEdit.imageFile !== null;
    setErrors(tempErrors);
    return Object.values(tempErrors).every((x) => x === true);
  };

  const applyErrorClass = (field) =>
    field in errors && !errors[field] ? "is-invalid" : "";

  const handleSaveOrUpdatePromotion = async () => {
    if (!validate()) {
      return; // Stop if validation fails
    }

    setIsSaving(true);
    try {
      const formData = new FormData();
      formData.append("promotionId", promoToEdit.promotionId);
      formData.append("userId", user.id);
      formData.append("title", promoToEdit.title || "Special Offer");
      formData.append("content", promoToEdit.content);
      if (promoToEdit.imageFile) {
        formData.append("imageFile", promoToEdit.imageFile);
      }

      if (isEditMode) {
        await apiService.update(config.UPDATEPROMOTION, formData);
      } else {
        await apiService.create(config.CREATEPROMOTION, formData);
      }

      handleAddEditModalClose();
      getPromotions();
    } catch (error) {
      // apiService will show error toast
    } finally {
      setIsSaving(false);
    }
  };

  const handleSendModalOpen = (promo) => {
    setSelectedPromo(promo);
    setShowSendModal(true);
  };
  const handleSendModalClose = () => {
    setShowSendModal(false);
    setSelectedPromo(null);
  };

  const handleViewModalOpen = (promo) => {
    setPromoToView(promo);
    setShowViewModal(true);
  };
  const handleViewModalClose = () => setShowViewModal(false);

  const handleDeleteModalOpen = (promo) => {
    setPromoToDelete(promo);
    setShowDeleteModal(true);
  };
  const handleDeleteModalClose = () => setShowDeleteModal(false);

  const handleDeletePromotion = async () => {
    if (!promoToDelete) return;
    try {
      await apiService.remove(
        config.DELETEPROMOTION,
        promoToDelete.promotionId
      );
      handleDeleteModalClose();
      getPromotions();
    } catch (error) {
      // apiService will show error toast
    }
  };

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="card">
              <div className="p-2">
                <MarketingTabs pageName="PROMOTION"></MarketingTabs>
              </div>
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h4>Promotional Campaigns</h4>
                  <Button variant="primary" onClick={handleAddModalOpen}>
                    <i className="ri-add-line align-bottom me-1"></i> Add
                    Promotion
                  </Button>
                </div>

                {isLoading ? (
                  <div className="text-center py-5">Loading...</div>
                ) : (
                  <div className="row g-4">
                    {promotions.length > 0 ? (
                      promotions.map((promo) => (
                        <div
                          className="col-xl-3 col-md-4 col-sm-6"
                          key={promo.promotionId}
                        >
                          <div className="card h-100">
                            <img
                              src={promo.imageSrc}
                              className="card-img-top"
                              alt={promo.title}
                              style={{ height: "200px", objectFit: "cover" }}
                            />
                            <div className="card-body">
                              <h5 className="card-title">{promo.title}</h5>
                              <p className="card-text text-muted small">
                                {promo.content.substring(0, 80)}...
                              </p>
                            </div>

                            <div className="card-footer">
                              <div className="d-flex justify-content-between">
                                <button
                                  className="btn btn-sm btn-info"
                                  onClick={() => handleViewModalOpen(promo)}
                                  title="View"
                                >
                                  <i className="ri-eye-fill"></i> View
                                </button>
                                <button
                                  className="btn btn-sm btn-warning"
                                  onClick={() => handleEditModalOpen(promo)}
                                  title="Edit"
                                >
                                  <i className="ri-pencil-fill"></i> Edit
                                </button>
                                <button
                                  className="btn btn-sm btn-success"
                                  onClick={() =>
                                    handleWhatsappShareClick(promo)
                                  }
                                  title="Share via WhatsApp"
                                >
                                  <i className="ri-whatsapp-line"></i> Share
                                </button>
                                <button
                                  className="btn btn-sm btn-danger"
                                  onClick={() => handleDeleteModalOpen(promo)}
                                  title="Delete"
                                >
                                  <i className="ri-delete-bin-fill"></i>
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="col-12 text-center py-5 text-muted">
                        <h5>No promotions found.</h5>
                        <p>
                          Click "Add Promotion" to create your first campaign.
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={showAddEditModal} onHide={handleAddEditModalClose} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? "Edit Promotion" : "Create New Promotion"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">
              Promotional Poster*{" "}
              {isEditMode && "(Leave blank to keep existing)"}
            </label>
            <input
              type="file"
              className={`form-control ${applyErrorClass("imageFile")}`}
              accept="image/jpeg, image/png"
              onChange={handleImageChange}
            />
            {errors.imageFile === false && (
              <div className="invalid-feedback d-block">
                Please upload an image for the promotion.
              </div>
            )}
          </div>

          {previewImage && (
            <img
              src={previewImage}
              alt="Preview"
              className="img-fluid rounded mt-2"
            />
          )}
          <div className="mb-3">
            <label className="form-label">
              Title <span className="text-muted">(Optional)</span>
            </label>
            <input
              type="text"
              className="form-control"
              name="title"
              value={promoToEdit.title}
              onChange={handlePromoInputChange}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">
              Content <span className="text-muted">(Optional)</span>
            </label>
            <textarea
              className="form-control"
              name="content"
              value={promoToEdit.content}
              onChange={handlePromoInputChange}
              rows="4"
            ></textarea>
          </div>

          {/* --- STEP 3: Update the JSX for the image input --- */}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleAddEditModalClose}>
            Close
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveOrUpdatePromotion}
            disabled={isSaving}
          >
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </Modal.Footer>
      </Modal>

      {selectedPromo && (
        <CustomerShareModal
          show={showSendModal}
          onHide={handleSendModalClose}
          promotion={selectedPromo}
          user={user}
        />
      )}

      <Modal
        show={showViewModal}
        onHide={handleViewModalClose}
        centered
        size="md"
      >
        <Modal.Header closeButton>
          <Modal.Title>{promoToView?.title}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <img
            src={promoToView?.imageSrc}
            alt={promoToView?.title}
            className="img-fluid rounded mb-3"
          />
          <h6>Content:</h6>
          <p>{promoToView?.content}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleViewModalClose}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <DeleteAlert
        show={showDeleteModal}
        handleClose={handleDeleteModalClose}
        handleDelete={handleDeletePromotion}
        itemName={promoToDelete?.title}
      />
    </div>
  );
};

export default Promotions;
